import { LocalStorageKeys } from '@shared/constants/local-storage-keys';
import { LastMapPosition } from '../interfaces/map-data';
import { inject, Injectable } from '@angular/core';
import { ReloadDetectionService } from '@core/services/reload-detection.service';
import { MapDataService } from './map-data.service';
import { SanitizationService } from '@shared/services/sanitization.service';
import { ClipboardService } from './clipboard.service';
import * as L from 'leaflet';

@Injectable()
export class MapStateService {
  private reloadDetectionService = inject(ReloadDetectionService);
  private mapDataService = inject(MapDataService);
  private sanitizationService = inject(SanitizationService);
  private clipboardService = inject(ClipboardService);

  readonly defaultMapCenter: [number, number] = [63.4305, 10.3951];

  // resolved once, reused everywhere
  private readonly reloadHappened = this.reloadDetectionService.isPageReload();
  private readonly urlMapState = this.mapDataService.getMapStateFromURL();

  initMap(mapId: string): L.Map {
    const { center, zoom } = this.resolveInitialState();

    const map = L.map(mapId, {
      zoomControl: false,
      doubleClickZoom: true,
    }).setView(center, zoom);

    const safeLabel = this.sanitizationService.sanitize(this.urlMapState?.label);

    if (this.urlMapState?.lat && this.urlMapState.lng && this.urlMapState.zoom) {
      this.clipboardService.addMarkerToMap(
        map,
        L.latLng({ lat: this.urlMapState.lat, lng: this.urlMapState.lng }),
        safeLabel,
        true,
        true,
      );
    }

    return map;
  }

  isFirstLoad(): boolean {
    return !this.reloadHappened && !this.urlMapState;
  }

  saveLastPosition(center: L.LatLng, zoom: number): void {
    localStorage.setItem(
      LocalStorageKeys.LastMapPosition,
      JSON.stringify({ lastZoom: zoom, lastCenter: center } satisfies LastMapPosition),
    );
  }

  private resolveInitialState(): { center: [number, number]; zoom: number } {
    const savedMapStateJSON = localStorage.getItem(LocalStorageKeys.LastMapPosition);
    const savedMapState: LastMapPosition | null =
      this.reloadHappened && savedMapStateJSON ? JSON.parse(savedMapStateJSON) : null;

    const defaultZoom = 9;
    let center: [number, number] = this.defaultMapCenter;
    let zoom = this.urlMapState?.lat && this.urlMapState.lng ? 12 : defaultZoom;

    if (this.urlMapState) {
      center = [this.urlMapState.lat, this.urlMapState.lng];
      if (this.urlMapState.zoom) {
        zoom = this.urlMapState.zoom;
      }
    } else if (savedMapState) {
      center = [savedMapState.lastCenter.lat, savedMapState.lastCenter.lng];
      zoom = savedMapState.lastZoom;
    }

    return { center, zoom };
  }
}
